import { useCallback, useEffect, useState } from 'react'
import type { RiskReductionMeasure } from '@/shared/hooks/api/machineryDirectiveCompliance'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import type { RiskReductionMeasureFormData } from './RiskReductionMeasureForm.schema'
import { toRiskReductionMeasureFormData } from './riskReductionMeasuresUtils'

type EditRiskReductionMeasureTypeDialogProps = {
  isOpen: boolean
  onClose: () => void
  riskReductionMeasure: RiskReductionMeasure
  onSave: (data: RiskReductionMeasureFormData) => void
  isSaving: boolean
}

type MeasureTypeOption = {
  value: RiskReductionMeasureFormData['type']
  label: string
}

export function EditRiskReductionMeasureTypeDialog({
  isOpen,
  onClose,
  riskReductionMeasure,
  onSave,
  isSaving,
}: EditRiskReductionMeasureTypeDialogProps) {
  const { t } = useTranslation('machineryDirectiveCompliance')
  const [type, setType] = useState<RiskReductionMeasureFormData['type']>(
    riskReductionMeasure.type
  )

  const measureTypeOptions: MeasureTypeOption[] = [
    {
      value: 'ISD',
      label: t('riskReductionMeasuresLibraryPage.form.measureTypes.ISD'),
    },
    {
      value: 'SCP',
      label: t('riskReductionMeasuresLibraryPage.form.measureTypes.SCP'),
    },
    {
      value: 'PPE',
      label: t('riskReductionMeasuresLibraryPage.form.measureTypes.PPE'),
    },
    {
      value: 'OI',
      label: t('riskReductionMeasuresLibraryPage.form.measureTypes.OI'),
    },
  ]

  useEffect(() => {
    if (!isOpen) {
      return
    }
    setType(riskReductionMeasure.type)
  }, [isOpen, riskReductionMeasure.type])

  const handleClose = useCallback(() => {
    onClose()
  }, [onClose])

  const handleSubmit = () => {
    if (isSaving) {
      return
    }

    onSave({
      ...toRiskReductionMeasureFormData(riskReductionMeasure),
      type,
    })
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className='sm:max-w-lg' showCloseButton={!isSaving}>
        <DialogHeader>
          <DialogTitle>
            {t('riskReductionMeasuresLibraryPage.rrmDetail.editTypeDialog.title')}
          </DialogTitle>
        </DialogHeader>

        <div className='space-y-2'>
          <Label htmlFor='rrm-edit-type'>
            {t('riskReductionMeasuresLibraryPage.form.type')}{' '}
            <span className='text-destructive'>*</span>
          </Label>
          <Select
            value={type}
            onValueChange={(value) => {
              if (!value) {
                return
              }
              setType(value as RiskReductionMeasureFormData['type'])
            }}
            items={measureTypeOptions}
          >
            <SelectTrigger id='rrm-edit-type' className='w-full'>
              <SelectValue
                placeholder={t(
                  'riskReductionMeasuresLibraryPage.form.typePlaceholder'
                )}
              />
            </SelectTrigger>
            <SelectContent>
              {measureTypeOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <DialogFooter>
          <Button variant='outline' onClick={handleClose} disabled={isSaving}>
            {t('common:actions.cancel')}
          </Button>
          <Button onClick={handleSubmit} disabled={isSaving}>
            {t('riskReductionMeasuresLibraryPage.formDialog.updateButton')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
